/** What the Call Scripts screens share (plan E08-T06): a new app, its secret shown once, a hook test. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Copy } from "lucide-react";
import { useId, useState, type FormEvent } from "react";
import { data, type Schemas } from "../api/client";
import { useBff, useMe } from "../app/session";
import { useT, type MessageId } from "../i18n";
import { Button } from "../ui/Button";
import { ErrorState } from "../ui/states";
import { useApiQuery } from "../app/Page";
import { useEndpoints } from "../agents/builder/models";

export type ScriptApp = Schemas["ScriptAppOut"];
export type ScriptVersion = Schemas["ScriptAppVersionOut"];
export type HookTest = Schemas["HookTestOut"];
type Dialect = "swaralo" | "jambonz-0.9";

export const field = "rounded-md border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1.5 text-sm";

export function VersionStatus({ status }: { status: ScriptVersion["status"] }) {
  const t = useT();
  const tone = status === "published" ? "border-[var(--color-success)]" : "border-[var(--color-border)]";
  return <span className={`rounded-full border px-2 py-0.5 text-xs ${tone}`}>{t(`agents.status.${status}` as MessageId)}</span>;
}

/** A webhook secret, shown this once: it signs every hook call, and is never shown again. */
export function SecretOnce({ secret, children }: { secret: string; children?: React.ReactNode }) {
  const t = useT();
  const id = useId();
  const [copied, setCopied] = useState(false);
  return (
    <section aria-labelledby={`${id}-title`} role="region"
      className="space-y-2 rounded-lg border border-[var(--color-warning)] p-4">
      <h2 id={`${id}-title`} className="font-semibold">{t("scripts.secret.title")}</h2>
      <p className="text-sm">{t("scripts.secret.body")}</p>
      <div className="flex flex-wrap items-center gap-2">
        <code aria-label={t("scripts.secret.label")} className="break-all rounded bg-[var(--color-surface)] px-2 py-1 font-mono text-xs">
          {secret}
        </code>
        <Button variant="secondary" onClick={() => {
          void navigator.clipboard?.writeText(secret).then(() => setCopied(true), () => setCopied(false));
        }}>
          <Copy size={16} aria-hidden="true" />
          {t("scripts.secret.copy")}
        </Button>
        {copied && <span role="status" className="text-xs">{t("scripts.secret.copied")}</span>}
      </div>
      {children}
    </section>
  );
}

/** Name, description, the call hook, its method and its verb format: version 1, a draft. */
export function NewScriptApp({ onCreated }: { onCreated: (made: Schemas["ScriptAppCreated"]) => void }) {
  const t = useT();
  const { api } = useBff();
  const tenant = useMe().tenant_id;
  const queries = useQueryClient();
  const id = useId();
  const [form, setForm] = useState({ name: "", description: "", url: "", method: "POST" as "POST" | "GET", status: "",
    dialect: "swaralo" as Dialect, language: "en-US" });
  const create = useMutation({
    mutationFn: () => data(api.POST("/v1/script-apps", {
      body: {
        name: form.name.trim(), description: form.description,
        spec: {
          call_hook: { url: form.url.trim(), method: form.method },
          ...(form.status.trim() ? { status_hook: { url: form.status.trim() } } : {}),
          dialect: form.dialect, defaults: { language: form.language.trim() || "en-US" },
        },
      },
    })),
    onSuccess: async (made) => {
      await queries.invalidateQueries({ queryKey: ["scripts", tenant] });
      onCreated(made);
    },
  });
  const ready = form.name.trim() && /^https?:\/\/\S+$/.test(form.url.trim());
  const input = (key: "name" | "description" | "url" | "status" | "language", label: MessageId, extra: Record<string, unknown> = {}) => (
    <div className="space-y-1">
      <label htmlFor={`${id}-${key}`} className="block text-sm font-medium">{t(label)}</label>
      <input id={`${id}-${key}`} className={`${field} w-full`} value={form[key]}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })} {...extra} />
    </div>
  );
  return (
    <form aria-label={t("scripts.new")} className="space-y-3 rounded-lg border border-[var(--color-border)] p-4"
      onSubmit={(e: FormEvent) => {
        e.preventDefault();
        if (ready) create.mutate();
      }}>
      <div className="grid gap-3 sm:grid-cols-2">
        {input("name", "scripts.name", { maxLength: 100, required: true, placeholder: "main-menu" })}
        {input("description", "scripts.descriptionLabel", { maxLength: 1000 })}
        {input("url", "scripts.callHook", { type: "url", required: true, placeholder: "https://app.example.com/voice" })}
        <div className="space-y-1">
          <label htmlFor={`${id}-method`} className="block text-sm font-medium">{t("scripts.method")}</label>
          <select id={`${id}-method`} className={`${field} w-full`} value={form.method}
            onChange={(e) => setForm({ ...form, method: e.target.value as "POST" | "GET" })}>
            <option value="POST">POST</option>
            <option value="GET">GET</option>
          </select>
        </div>
        {input("status", "scripts.statusHook", { type: "url", placeholder: "https://app.example.com/status" })}
        <div className="space-y-1">
          <label htmlFor={`${id}-dialect`} className="block text-sm font-medium">{t("scripts.dialect")}</label>
          <select id={`${id}-dialect`} className={`${field} w-full`} value={form.dialect} aria-describedby={`${id}-dialect-hint`}
            onChange={(e) => setForm({ ...form, dialect: e.target.value as Dialect })}>
            <option value="swaralo">{t("scripts.dialect.swaralo")}</option>
            <option value="jambonz-0.9">{t("scripts.dialect.jambonz")}</option>
          </select>
          <p id={`${id}-dialect-hint`} className="text-xs text-[var(--color-muted)]">{t("scripts.dialectHint")}</p>
        </div>
        {input("language", "scripts.language", { maxLength: 35 })}
      </div>
      {create.isError && <ErrorState error={create.error} />}
      <Button type="submit" disabled={!ready || create.isPending}>{t("scripts.create")}</Button>
    </form>
  );
}

/** How the call hook answered a sample call: in time, with verbs a call would run, or why not. */
export function HookTestResult({ result }: { result: HookTest }) {
  const t = useT();
  return (
    <div role="status" aria-label={t("scripts.test.result")}
      className={`space-y-2 rounded-lg border p-3 text-sm ${result.ok ? "border-[var(--color-success)]" : "border-[var(--color-danger)]"}`}>
      <p className="font-medium">
        {result.ok ? t("scripts.test.ok", { ms: result.duration_ms })
          : result.error ? t("scripts.test.failed", { error: result.error })
          : t("scripts.test.badVerbs", { n: result.problems.length })}
      </p>
      <p className="break-all text-xs text-[var(--color-muted)]">
        {t("scripts.test.detail", { url: result.url, status: result.status ?? "—", attempts: result.attempts })}
      </p>
      {result.problems.length > 0 && (
        <ul aria-label={t("scripts.test.problems")} className="list-disc pl-5 text-xs">
          {result.problems.map((p, i) => (
            <li key={i}>{t("scripts.test.problem", { index: p.index ?? "—", path: p.path || "—", message: p.message })}</li>
          ))}
        </ul>
      )}
      {result.warnings.length > 0 && (
        <ul aria-label={t("scripts.test.warnings")} className="list-disc pl-5 text-xs">
          {result.warnings.map((w) => <li key={w}>{w}</li>)}
        </ul>
      )}
      {result.answer !== null && result.answer !== undefined && (
        <details>
          <summary className="cursor-pointer text-xs">{t("scripts.test.answer")}</summary>
          <pre className="mt-2 max-h-64 overflow-auto text-xs">{JSON.stringify(result.answer, null, 2)}</pre>
        </details>
      )}
    </div>
  );
}

type Classifier = { provider?: string; endpoint?: string; credential?: string; model?: string };
const CLASSIFIERS = ["openai", "groq", "openai-compatible", "endpoint", "fake"] as const;

/** What decides a gather's intents (E10-T04): the app's `defaults.classifier`, edited in the spec. */
export function ClassifierChoice({ spec, onChange }: { spec: Record<string, unknown>; onChange: (spec: Record<string, unknown>) => void }) {
  const t = useT();
  const id = useId();
  const llms = useEndpoints("llm");
  const own = useEndpoints("classifier");
  const credentials = useApiQuery("credentials", (a) => a.GET("/v1/credentials", { params: { query: { limit: 200 } } }));
  const defaults = (spec.defaults ?? {}) as Record<string, unknown>;
  const current = (defaults.classifier ?? {}) as Classifier;
  const set = (next: Classifier | null) => {
    const cleaned = next && Object.fromEntries(Object.entries(next).filter(([, v]) => v));
    const rest = Object.fromEntries(Object.entries(defaults).filter(([key]) => key !== "classifier"));
    onChange({ ...spec, defaults: cleaned ? { ...rest, classifier: cleaned } : rest });
  };
  const endpoints = [...own, ...llms];
  const pick = (provider: string) => {
    if (!provider) set(null);
    else if (provider === "endpoint") set({ provider, endpoint: endpoints[0]?.value });
    else if (provider === "fake") set({ provider });
    else set({ provider, credential: current.credential, model: current.model });
  };
  return (
    <fieldset className="space-y-2 rounded-md border border-[var(--color-border)] p-3">
      <legend className="px-1 text-sm font-medium">{t("scripts.classifier")}</legend>
      <p className="text-xs text-[var(--color-muted)]">{t("scripts.classifierHint")}</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="space-y-1">
          <label htmlFor={`${id}-provider`} className="block text-sm">{t("scripts.classifierProvider")}</label>
          <select id={`${id}-provider`} className={`${field} w-full`} value={current.provider ?? ""} onChange={(e) => pick(e.target.value)}>
            <option value="">{t("scripts.classifierNone")}</option>
            {CLASSIFIERS.map((p) => <option key={p} value={p}>{t(`scripts.classifier.${p}` as MessageId)}</option>)}
          </select>
        </div>
        {current.provider === "endpoint" && (
          <div className="space-y-1">
            <label htmlFor={`${id}-endpoint`} className="block text-sm">{t("builder.endpoint")}</label>
            <select id={`${id}-endpoint`} className={`${field} w-full`} value={current.endpoint ?? ""}
              onChange={(e) => set({ ...current, endpoint: e.target.value })}>
              {endpoints.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        )}
        {current.provider && !["endpoint", "fake"].includes(current.provider) && (
          <>
            <div className="space-y-1">
              <label htmlFor={`${id}-credential`} className="block text-sm">{t("models.credential")}</label>
              <select id={`${id}-credential`} className={`${field} w-full`} value={current.credential ?? ""}
                onChange={(e) => set({ ...current, credential: e.target.value })}>
                <option value="" />
                {(credentials.data?.data ?? []).map((c: Schemas["CredentialOut"]) => <option key={c.id} value={c.name}>{c.name}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label htmlFor={`${id}-model`} className="block text-sm">{t("models.model")}</label>
              <input id={`${id}-model`} className={`${field} w-full font-mono`} value={current.model ?? ""} spellCheck={false}
                onChange={(e) => set({ ...current, model: e.target.value })} />
            </div>
          </>
        )}
      </div>
    </fieldset>
  );
}
